import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import Editor from '@monaco-editor/react';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import wordList from './wordListV3c.json'; // Adjust the path to your JSON file as necessary
import EmojiPicker from 'emoji-picker-react';


const default_suggestion = `# Title, - list, > blockquoute, *italic*, **bold**, [link], !img`;
const debug = false;

const Markdown = () => {
  const [markdown, setMarkdown] = useState('');
  const [fileInputDisabled, setFileInputDisabled] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [suggestions, setSuggestions] = useState(default_suggestion);
  const fileInputRef = useRef(null);
  const [useLocalStorage, setUseLocalStorage] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const editorRef = useRef(null);

  const onMountEditor = (editor, monaco) => {
    console.log('On mount.');
    editorRef.current = editor;
    // Define provideCompletionItems function inside onMount to capture 'monaco' in scope
    const provideCompletionItems = (model, position) => {
      const word = model.getWordUntilPosition(position);
      console.log(word);
      console.log(`Length: ${(word.endColumn - word.startColumn)}`);

      if ((word.endColumn - word.startColumn) < 4) {
        console.log('Returning empty suggestions.');
        return { suggestions: [] };
      }

      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn
      };

      // Filter the word list based on the current word to generate suggestions
      const suggestions = wordList
        .filter(w => w.toLowerCase().startsWith(word.word.toLowerCase()))
        .slice(0, 50) // Limit to first 50 matches for performance
        .map(matchingWord => ({
          label: matchingWord,
          kind: monaco.languages.CompletionItemKind.Text,
          insertText: word.word + matchingWord.substring(word.word.length),
          range: range
        }));

      console.log(suggestions);

      return { suggestions: suggestions };
    };

    // Register the completion item provider using the 'monaco' instance
    monaco.languages.registerCompletionItemProvider('markdown', {
      provideCompletionItems,
    });
  };

  const encryptData = (data) => {
    if (debug) {
      console.log(`Encrypting using key ${secretKey}`);
    }
    return AES.encrypt(data, secretKey).toString();
  };

  const decryptData = (ciphertext, key) => {
    if (debug) {
      console.log(`Ciphertext: ${ciphertext}`);
      console.log(`Decrypting using key ${key}`);
    }
    const bytes = AES.decrypt(ciphertext, key);
    return bytes.toString(Utf8);
  };

  // Function to toggle the emoji picker's visibility
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker)
  };

  // Function to handle emoji selection

  const onEmojiClick = (emojiObject, event) => {
    var selection = editorRef.current.getSelection();
    var id = { major: 1, minor: 1 };
    var text = emojiObject.emoji;
    console.log(text);
    var op = { identifier: id, range: selection, text: text, forceMoveMarkers: true };
    editorRef.current.executeEdits("my-source", [op]);

    // Hide the emoji picker after selection
    setShowEmojiPicker(false);
  };


  const handleInputChange = (value, event) => {
    setMarkdown(value);
    setFileInputDisabled(true);
    setUnsavedChanges(true);
    if (useLocalStorage) {
      const encryptedData = encryptData(value);
      if (debug) {
        console.log(`Storing ${encryptedData}`);
      }
      localStorage.setItem('md', encryptedData);
    }
  };

  const renderMarkdown = () => {
    return { __html: marked(markdown) };
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(markdown);
  };

  const saveToFile = () => {
    try {
      const element = document.createElement("a");
      const file = new Blob([markdown], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = "markdown_content.txt";
      document.body.appendChild(element);
      element.click();
      const downloadConfirmation = window.confirm("File downloaded successfully?");
      if (downloadConfirmation) {
        setUnsavedChanges(false);
        setFileInputDisabled(false);
      }
    } catch (error) {
      console.error("Error saving file:", error);
    }
  };

  const loadFromFile = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setMarkdown(content);
      setUnsavedChanges(true);
    };
    reader.readAsText(file);
  };

  const handleFileInputClick = () => {
    if (fileInputDisabled) {
      setFileInputDisabled(false);
    }
  };

  const handleUseLocalStorageChecked = () => {
    const encryptedContent = localStorage.getItem('md');
    if (encryptedContent) {
      alert("There is already data in localStorage. Please click on 'Load from localStorage' to access it.");
    } else {
      var msg = "Warning: This is an experimental feature that uses localStorage to save the contents of the Markdown editor.";
      msg += "The secret key you provide will not be saved or retrievable.";
      msg += "This website cannot be held responsible for any data loss. Do you want to proceed?";
      const confirmation = window.confirm(msg);
      if (confirmation) {
        let key = prompt("Enter a secret key to encrypt your data:", "");
        if (key) {
          setSecretKey(key);
          setUseLocalStorage(true);
        }
      }
    }
  };

  const handleLoadFromLocalStorage = () => {
    const encryptedContent = localStorage.getItem('md');
    if (encryptedContent) {
      let key = prompt("Enter your secret key to decrypt the data:", "");
      if (debug) {
        console.log(`Secret key: ${key}`);
      }
      if (key) {
        setSecretKey(key);
        try {
          const decryptedContent = decryptData(encryptedContent, key);
          if (!decryptedContent) {
            alert("Decrypted content is empty, did you type the correct key?");
          } else {
            setMarkdown(decryptedContent);
            if (debug) {
              console.log(`encryptedContent: ${encryptedContent}`)
              console.log(`decryptedContent: ${decryptedContent}`)
            }
            setUseLocalStorage(true);
          }
        } catch (error) {
          alert("Failed to decrypt data. Wrong key or corrupted data.");
        }
      } else {
        alert("You must enter a key to decrypt your data.");
      }
    }
  };

  const handleClearLocalStorag = () => {
    var msg = "Warning: This will clear localStorage. Your data will be lost. This can not be undone. ";
    msg += "Do you want to proceed?";
    const confirmation = window.confirm(msg);
    if (confirmation) {
      setUseLocalStorage(false);
      localStorage.clear();
    }
  };

  useEffect(() => {

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        setSuggestions(default_suggestion);
      }
      if (event.key === "#") {
        setSuggestions("# Title, ## h2, ### h3");
      }
      if (event.key === "*") {
        setSuggestions("*italic*, **bold**, * bullet");
      }
      if (event.key === "-") {
        setSuggestions("- bullet, --- line");
      }
      if (event.key === "[") {
        setSuggestions(`[inline link](https://url.com/ "Title"), [reference], [reference]:https://url.com, ![alt text](https://url.com/img.jpg "Title")`);
      }
      if (event.key === "!") {
        setSuggestions(`![alt text](https://url.com/img.jpg "Title")`);
      }
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault(); // Prevent the default save dialog
        saveToFile(); // Call your saveToFile function
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === "Backspace" || event.key === "Delete") {
        setSuggestions(default_suggestion);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {

    const handleBeforeUnload = (event) => {
      if (unsavedChanges && !useLocalStorage) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line
  }, [unsavedChanges]);

  return (
    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
      <div style={{ width: '95%', padding: '0.3rem', margin: '0 auto' }}>
        <div id="title" style={{ color: '#fff', marginBottom: '0.5rem', textAlign: 'left', marginLeft: '0.5rem' }}>
          Yet another Markdown editor v0.1B
        </div>
        <div id="controls" style={{ marginBottom: '0.5rem', textAlign: 'left', marginLeft: '0.5rem' }}>
          <button onClick={copyToClipboard}>Copy to Clipboard</button>
          <button onClick={saveToFile}>Save to file [ctrl+s]</button>
          <input
            type="file"
            id="file-input"
            style={{ display: 'none' }}
            onChange={loadFromFile}
            onClick={handleFileInputClick}
            ref={fileInputRef}
            accept=".txt"
            disabled={fileInputDisabled}
          />
          <button onClick={() => fileInputRef.current.click()} disabled={fileInputDisabled}>
            Load from file
          </button>
          <button onClick={toggleEmojiPicker}>
            Emoji
          </button>
          <button onClick={handleLoadFromLocalStorage}>
            Load from localStorage
          </button>
          <button onClick={handleClearLocalStorag}>
            Clear localStorage
          </button>
          <label style={{ color: '#fff', fontSize: '10px' }}>
            <input
              type="checkbox"
              checked={useLocalStorage}
              onChange={handleUseLocalStorageChecked}
            /> Use localStorage
          </label>          
        </div>
        {showEmojiPicker && (
          <div style={{ 
            zIndex: 1000,
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            alignItems: 'center',
            textAlign: 'center',
            justifyContent: 'center',
            backgroundColor: '#000',
            border: 'solid',
            borderColor: '#fff',
            borderRadius: '10px',
            padding: '10px',
           }}>
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              skinTonesDisabled='true'
            />
            <button style={{marginTop: '5px'}} onClick={toggleEmojiPicker}>
              Close
            </button>
          </div>
        )}
        <div id="content-area" style={{ display: 'flex', flexDirection: 'column', height: 'calc(90vh - 2rem)' }}>
          <div id="suggestions" style={{ color: '#fff', fontSize: '10px', marginBottom: '0.5rem', textAlign: 'left', marginLeft: '0.5rem' }}>
            Suggestions: {suggestions}
          </div>
          <div id="markdown_editor" style={{ display: 'flex', flex: 1 }}>
            <div id="editor" style={{ flex: '1', margin: '0 5px', border: '2px solid #ccc', borderRadius: '5px', overflow: 'hidden' }}>
              <Editor
                height="100%"
                defaultLanguage="markdown"
                theme="vs-dark"
                value={markdown}
                onChange={handleInputChange}
                onMount={onMountEditor}
                options={{
                  automaticLayout: true,
                  wordWrap: 'on',
                  minimap: { enabled: false },
                  suggestions: true,
                  fontSize: 12
                }}
              />
            </div>
            <div id="markdown" style={{ flex: '1', margin: '0 5px', color: '#ffffff', fontSize: '12px', border: '2px solid #ccc', borderRadius: '5px', textAlign: 'left', padding: '0.5rem', overflowY: 'auto' }}>
              <div dangerouslySetInnerHTML={renderMarkdown()} style={{ height: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default Markdown;
